import * as React from "react";
import {
    Typography,
    Box,
    Container,
    Button,
    MenuItem,
    CircularProgress,
    Chip,
    Paper,
} from "@mui/material";
import { connect } from "react-redux";
import { notification } from "antd";
import {
    sec_button,
    main_button,
    MainInput,
} from "../../app/components/Styles";
import { FoCard } from "./financeUi";
import ReactFileReader from "react-file-reader";
import Upload from "@mui/icons-material/Upload";
import Download from "@mui/icons-material/Download";
import { addTransactions } from "../../app/store/actions/whFinanceActions";
import { useNavigate } from "react-router-dom";
import {
    parseBlinkTransactionsSheet,
    parseBostaTransactionsSheet,
    parseCustomTransactionsCsv,
    parseFincartTransactionsSheet,
    parseShipbluTransactionsCsv,
} from "./transactionImportHelpers";

export const AddTransactions = ({
    addTransactions,
    isLoading,
}) => {
    const [file, setFile] = React.useState(null);
    const [source, setSource] = React.useState("Bosta");
    const navigate = useNavigate()

    const feeTypeOptions = ["None", "Handling", "Inbound", "Outbound", "Overstock", "Pick & Pack", "Material", "Large Order", "Transportation", "Order Pickup", "Repacking", "Other"];

    const handleDeleteFile = () => {
        setFile(null);
    };

    function timeout(delay) {
        return new Promise((res) => setTimeout(res, delay));
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        var reader = new FileReader();
        if (file) {
            reader.readAsText(file);
            reader.onload = async () => {
                try {
                    const transactions = parseCustomTransactionsCsv(reader.result);
                    addTransactions({ transactions, source: "Custom" }, navigate)
                } catch (err) {
                    notification.error({ message: err?.message || "Failed to parse file" });
                }
            };
        } else {
            notification.error({ message: "Please Upload a valid .csv file" });
        }
    };

    const handleShipbluSubmit = (event) => {
        event.preventDefault();
        var reader = new FileReader();
        if (file) {
            reader.readAsText(file);
            reader.onload = async () => {
                try {
                    const transactions = parseShipbluTransactionsCsv(reader.result);
                    addTransactions({ transactions, source: "Shipblu" }, navigate)
                } catch (err) {
                    notification.error({ message: err?.message || "Failed to parse file" });
                }
            };
        } else {
            notification.error({ message: "Please Upload a valid .csv file" });
        }
    };

    const handleBostaSubmit = (event) => {
        event.preventDefault();
        var reader = new FileReader();
        if (file) {
            reader.onload = async (e) => {
                try {
                    const transactions = parseBostaTransactionsSheet(e.target.result);
                    addTransactions({ transactions, source: "Bosta" }, navigate);
                } catch (err) {
                    notification.error({ message: err?.message || "Failed to parse file" });
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            notification.error({ message: "Please upload a valid Excel file (.xlsx or .xls)" });
        }
    };

    const handleFincartSubmit = (event) => {
        event.preventDefault();
        const reader = new FileReader();
        if (file) {
            reader.onload = async (e) => {
                try {
                    // Parse the Fincart invoice in-browser — never upload the workbook.
                    // POST /Finance/transactions expects JSON: { source, transactions[] }.
                    const fileBaseName = file.name?.replace(/\.[^.]+$/, "") || "FINCART";
                    const transactions = parseFincartTransactionsSheet(e.target.result, fileBaseName);
                    if (!Array.isArray(transactions) || transactions.length === 0) {
                        notification.error({
                            message: "No Fincart transactions found in this file",
                            description: "Expected columns such as tracking, orderNumber, reference_number, amount, shipping_fees, and status.",
                        });
                        return;
                    }
                    addTransactions({ source: "Fincart", transactions }, navigate);
                } catch (err) {
                    notification.error({ message: err?.message || "Failed to parse file" });
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            notification.error({ message: "Please upload a valid Excel file (.xlsx)" });
        }
    };

    const handleBlinkSubmit = (event) => {
        event.preventDefault();
        const reader = new FileReader();
        if (file) {
            reader.onload = async (e) => {
                try {
                    const fileBaseName = file.name?.replace(/\.[^.]+$/, "") || "BLINK";
                    const transactions = parseBlinkTransactionsSheet(e.target.result, fileBaseName);
                    if (!transactions.length) {
                        notification.error({ message: "No Blink payout orders found in this file" });
                        return;
                    }
                    addTransactions({ transactions, source: "Blink" }, navigate);
                } catch (err) {
                    notification.error({ message: err?.message || "Failed to parse file" });
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            notification.error({ message: "Please upload a valid Excel file (.xlsx)" });
        }
    };

    const submitHandler = {
        Bosta: handleBostaSubmit,
        Shipblu: handleShipbluSubmit,
        Fincart: handleFincartSubmit,
        Blink: handleBlinkSubmit,
    }[source] || handleSubmit;

    const acceptsWorkbook = source === "Bosta" || source === "Fincart" || source === "Blink";
    const uploadLabel = source === "Bosta" ? ".xls or .xlsx" : acceptsWorkbook ? ".xlsx" : ".csv";

    const handleFiles = (files) => {
        setFile(files[0]);
    };

    return (
        <FoCard className="fo-form" title="Import a file" note="Bosta, Shipblu, Fincart, Blink, or a custom CSV. A single-transaction form is not on this API.">

                <Box sx={{ mb: 3 }}>
                    <Button
                        fullWidth
                        sx={{ ...sec_button, py: 1.5 }}
                        href="https://firebasestorage.googleapis.com/v0/b/apps-static-storage.firebasestorage.app/o/CHAINZ%2Ftransactions_list_sample.csv?alt=media&token=6b660492-9bb3-4963-9e96-4e76f66af76b"
                        target="_blank"
                        startIcon={<Download />}
                    >
                        Download CSV Sample
                    </Button>
                </Box>

                <Box
                    component="form"
                    onSubmit={submitHandler}
                    sx={{ mt: 3 }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                            Transaction Source *
                        </Typography>
                        <MainInput
                            select
                            defaultValue={source}
                            onChange={({ target }) => setSource((target.value))}
                            fullWidth
                        >
                            <MenuItem value={"Bosta"}>Bosta</MenuItem>
                            <MenuItem value={"Shipblu"}>ShipBlu</MenuItem>
                            <MenuItem value={"Fincart"}>Fincart</MenuItem>
                            <MenuItem value={"Blink"}>Blink</MenuItem>
                            <MenuItem value={"Custom"}>Custom</MenuItem>
                        </MainInput>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                            Upload File *
                        </Typography>
                        <ReactFileReader
                            handleFiles={handleFiles}
                            fileTypes={source === "Bosta" ? [".xls", ".xlsx"] : acceptsWorkbook ? [".xlsx"] : ".csv"}
                        >
                            <Button fullWidth sx={{ ...sec_button, py: 1.5 }} startIcon={<Upload />}>
                                Upload {uploadLabel} File
                            </Button>
                        </ReactFileReader>
                        {file && (
                            <Box sx={{ mt: 2 }}>
                                <Chip 
                                    label={file.name} 
                                    onDelete={handleDeleteFile}
                                    sx={{ 
                                        backgroundColor: 'var(--secColor)',
                                        color: 'white',
                                        '& .MuiChip-deleteIcon': {
                                            color: 'white'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{ ...main_button, py: 1.5, mt: 2 }}
                        disabled={isLoading || !file}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Add Transactions"}
                    </Button>
                </Box>
        </FoCard>
    );
};

const mapStateToProps = (state) => ({
    isLoading: state?.wait?.isLoading,
});

const mapDispatchToProps = { addTransactions };

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AddTransactions);
